/**
 * Máscaras e validações de campo, espelhando `src/common/documento.util.ts` do
 * servidor.
 *
 * Duplicar a regra aqui é proposital: o usuário vê o erro enquanto digita, sem
 * ida ao servidor. A validação que **vale** continua sendo a do backend — esta
 * é conveniência, e some junto com o JavaScript.
 */

export function soDigitos(v: string): string {
  return v.replace(/\D/g, '');
}

export function mascaraCpf(v: string): string {
  const d = soDigitos(v).slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

export function mascaraCnpj(v: string): string {
  const d = soDigitos(v).slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

export function mascaraDocumento(v: string, pj: boolean): string {
  return pj ? mascaraCnpj(v) : mascaraCpf(v);
}

export function mascaraCep(v: string): string {
  const d = soDigitos(v).slice(0, 8);
  return d.replace(/^(\d{5})(\d)/, '$1-$2');
}

export function mascaraTelefone(v: string): string {
  const d = soDigitos(v).slice(0, 11);
  if (d.length <= 10) {
    return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');
}

// ── Validação ─────────────────────────────────────────────────────────────

function digitoVerificador(base: string, pesos: number[]): number {
  const soma = pesos.reduce((acc, peso, i) => acc + Number(base[i]) * peso, 0);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

export function cpfValido(v: string): boolean {
  const cpf = soDigitos(v);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  return (
    digitoVerificador(cpf, [10, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(cpf[9]) &&
    digitoVerificador(cpf, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(cpf[10])
  );
}

export function cnpjValido(v: string): boolean {
  const cnpj = soDigitos(v);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
  return (
    digitoVerificador(cnpj, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) ===
      Number(cnpj[12]) &&
    digitoVerificador(cnpj, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) ===
      Number(cnpj[13])
  );
}

export function documentoValido(v: string, pj: boolean): boolean {
  return pj ? cnpjValido(v) : cpfValido(v);
}

export function telefoneValido(v: string): boolean {
  const d = soDigitos(v);
  if (d.length !== 10 && d.length !== 11) return false;
  if (Number(d.slice(0, 2)) < 11) return false;
  if (d.length === 11 && d[2] !== '9') return false;
  return true;
}

/** Formata um documento vindo do servidor (sempre só dígitos). */
export function formatarDocumento(v: string): string {
  const d = soDigitos(v);
  if (d.length === 11) return mascaraCpf(d);
  if (d.length === 14) return mascaraCnpj(d);
  return v;
}

export const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO',
] as const;
